/**
 * HELP-ARTICLE.UTILS
 * Help articles published in Strapi (content type HelpArticle) : address to read one article and reading of the response
 */

export type HelpArticleAudience = "observateur" | "admin" | "tous"

//A zone of the page described in the guide, found again by its anchor (set once in Strapi, never changed)
export type HelpSectionMode = "tous" | "standard" | "gollac"

export interface HelpSection {
  anchor: string
  mode: HelpSectionMode //Shown for every display, only for the standard display (Karasek) or only for the Gollac display
  title: string
  body: string //HTML of the editor (to sanitize before display)
}

export interface HelpArticle {
  key: string
  title: string
  summary: string
  body: string //HTML of the editor : introduction of the guide (to sanitize before display)
  sections: HelpSection[]
  audience: HelpArticleAudience
  related: { key: string; title: string }[]
}

//Address to read one published article by its key (the key never changes, the slug can)
//Without the sections : for a Strapi that does not know them yet (the request with them would be refused)
export function getHelpArticleUrl(baseUrl: string, key: string, withSections: boolean = true): string {
  const params = new URLSearchParams()
  params.set("filters[key][$eq]", key)
  if (withSections) params.set("populate[sections]", "*")
  params.set("populate[relatedArticles][fields][0]", "key")
  params.set("populate[relatedArticles][fields][1]", "title")
  return `${baseUrl.replace(/\/+$/, "")}/api/help-articles?${params.toString()}`
}

//Read the response of Strapi : null if there is no published article or nothing to show
export function parseHelpArticle(json: any): HelpArticle | null {
  const row = json?.data?.[0]
  if (!row || typeof row.key !== "string" || typeof row.title !== "string") return null

  const body = typeof row.body === "string" ? row.body : ""
  const summary = typeof row.summary === "string" ? row.summary : ""

  //Sections without anchor or title can not be reached : they are ignored
  const sections: HelpSection[] = Array.isArray(row.sections)
    ? row.sections
        .filter((x: any) => x && typeof x.anchor === "string" && x.anchor && typeof x.title === "string")
        .map((x: any) => ({
          anchor: x.anchor,
          mode: x.mode === "standard" || x.mode === "gollac" ? x.mode : "tous",
          title: x.title,
          body: typeof x.body === "string" ? x.body : ""
        }))
    : []

  if (!body.trim() && !summary.trim() && sections.length === 0) return null

  const related = Array.isArray(row.relatedArticles)
    ? row.relatedArticles
        .filter((x: any) => x && typeof x.key === "string" && typeof x.title === "string")
        .map((x: any) => ({ key: x.key, title: x.title }))
    : []

  const audience: HelpArticleAudience = row.audience === "observateur" || row.audience === "admin" ? row.audience : "tous"

  return { key: row.key, title: row.title, summary, body, sections, audience, related }
}

//The articles for admins are hidden from the observers, the others are for everybody
export function isHelpArticleVisible(audience: HelpArticleAudience, userRole: string | null): boolean {
  return audience === "admin" ? userRole !== "OBSERVER" : true
}

//Is there a section for this zone ? (the hint of a zone is displayed only if its section is published)
export function hasHelpSection(article: HelpArticle | null | undefined, anchor: string): boolean {
  return !!article && article.sections.some((section) => section.anchor === anchor)
}

//Lines left in a guide to say where a capture will go ([CAPTURE : ...]) are never shown to the users
export function removeHelpPlaceholders(html: string): string {
  return html.replace(/<p>\s*<(?:em|i)>\s*\[CAPTURE[^\]]*\]\s*<\/(?:em|i)>\s*<\/p>/g, "")
}

//Link to another guide, written #guide-<key> in the text of a guide : returns the key, or null for any other link
export function getHelpGuideKeyFromHref(href: string | null | undefined): string | null {
  const match = /^#guide-([a-z0-9][a-z0-9.-]*)$/.exec(href ?? "")
  return match ? match[1] : null
}

//Section of the matrix guide about the comparison with the account : only when the option is on (account or role)
const ACCOUNT_COMPARE_ANCHOR = "comparaison-compte"

//Keep the sections that match the display of the survey (Gollac or standard) and the comparison with the account
export function filterHelpSectionsByMode(
  article: HelpArticle | null | undefined,
  isGollac: boolean,
  isAccountCompare: boolean = false
): HelpArticle | null | undefined {
  if (!article) return article
  return {
    ...article,
    sections: article.sections.filter(
      (section) =>
        (section.mode === "tous" || (section.mode === "gollac") === isGollac) &&
        (section.anchor !== ACCOUNT_COMPARE_ANCHOR || isAccountCompare)
    )
  }
}

//Page of the dashboard that a guide explains (the transversal guides, like the rules of calculation, have none)
//The two RPS guides have none : the page shows the one of its display, the other one is read in the window
const HELP_GUIDE_PAGES: Record<string, string> = {
  "dashboard.overview": "/dashboard/overview",
  "dashboard.participation": "/dashboard/participation",
  "dashboard.populations": "/dashboard/populations",
  "dashboard.questions": "/dashboard/questions",
  "dashboard.heatmap": "/dashboard/heatmap",
  "dashboard.history": "/dashboard/history",
  "dashboard.messages": "/dashboard/messages",
  "dashboard.nps": "/dashboard/nps"
}

export function getHelpGuidePage(key: string): string | null {
  return HELP_GUIDE_PAGES[key] ?? null
}

//Tab of the dashboard that a guide is about (its icon is the one of the guide), the two RPS guides share the RPS tab
const HELP_GUIDE_TAB_EXTRA: Record<string, string> = {
  "dashboard.rps": "/dashboard/rps",
  "dashboard.rps-gollac": "/dashboard/rps",
  "dashboard.compare": "/dashboard/compare"
}

export function getHelpGuideTab(key: string): string | null {
  return HELP_GUIDE_PAGES[key] ?? HELP_GUIDE_TAB_EXTRA[key] ?? null
}

//Short name of a guide, for the tooltip of a link that goes to it (the guides not listed get a general text)
const HELP_GUIDE_LABELS: Record<string, string> = {
  "observateur.demarrer": "Premiers pas",
  "dashboard.overview": "Vue d’ensemble",
  "dashboard.participation": "Participation",
  "dashboard.questions": "Questions",
  "dashboard.heatmap": "Matrice",
  "dashboard.history": "Historique",
  "dashboard.messages": "Commentaires",
  "dashboard.calcul": "Calcul des scores",
  "dashboard.nps": "NPS",
  "dashboard.benchmark": "Benchmark",
  "dashboard.rps": "RPS : affichage Karasek",
  "dashboard.rps-gollac": "RPS : affichage Gollac",
  "dashboard.confidentialite": "Confidentialité et anonymat",
  "dashboard.actions": "Plan d’action"
}

export function getHelpGuideTooltip(key: string): string {
  const label = HELP_GUIDE_LABELS[key]
  return label ? `Voir le guide : ${label}` : "Voir ce guide"
}
